import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, Typography } from "@mui/material";

import { AuthenticationService, NotificationService } from "../../services";
import { FCM_DEVICE_TOKEN_LS_KEY, USER_TYPE } from "../../constants";
import { AUTH_ROUTES } from "../../pages/auth/constants";
import { clearTableState } from "../../utils/tableStateStorage";

// Reasons a user can be blocked from the app
const BLOCK_REASON = {
  SUBSCRIPTION_EXPIRED: "SUBSCRIPTION_EXPIRED",
  NO_WEB_ACCESS: "NO_WEB_ACCESS",
  NO_PERMISSIONS: "NO_PERMISSIONS",
  NO_SUBSCRIPTION: "NO_SUBSCRIPTION",
  PLAN_NOT_STARTED: "PLAN_NOT_STARTED",
};

const DIALOG_CONFIG = {
  [BLOCK_REASON.SUBSCRIPTION_EXPIRED]: {
    title: "Subscription Expired",
    message: (type) =>
      type === USER_TYPE.ADMIN
        ? "Your company's subscription plan has expired. Please contact support to renew the plan. You will be redirected to the login page within"
        : "Your account access is currently restricted. Please contact your company admin to restore your access. You will be redirected to the login page within",
  },
  [BLOCK_REASON.NO_SUBSCRIPTION]: {
    title: "No Active Subscription",
    message: (type) =>
      type === USER_TYPE.ADMIN
        ? "There is no active subscription plan found for your company. Please contact support to assign a plan. You will be redirected to the login page within"
        : "Your account access is currently pending configuration. Please contact your company admin to complete your setup. You will be redirected to the login page within",
  },
  [BLOCK_REASON.PLAN_NOT_STARTED]: {
    title: "Plan Not Yet Active",
    message: (type) =>
      type === USER_TYPE.ADMIN
        ? "Your assigned subscription plan has a future start date and is not yet active. Please wait until the start date. You will be redirected to the login page within"
        : "Your account is scheduled to activate soon. Please contact your company admin for details or wait for activation. You will be redirected to the login page within",
  },
  [BLOCK_REASON.NO_WEB_ACCESS]: {
    title: "Web Access Not Enabled",
    message: (type) =>
      type === USER_TYPE.ADMIN
        ? "Your current subscription plan does not include web access. Please contact support to upgrade your plan. You will be redirected to the login page within"
        : "Your account is not currently enabled for web platform access. Please contact your company admin for access. You will be redirected to the login page within",
  },
  [BLOCK_REASON.NO_PERMISSIONS]: {
    title: "No Permissions Assigned",
    message: (type) =>
      type === USER_TYPE.ADMIN
        ? "You do not have any permissions assigned for your admin role. Please contact support. You will be redirected to the login page within"
        : "You do not have access permissions assigned. Please contact your company admin to configure your role. You will be redirected to the login page within",
  },
};

export const PermissionsGuard = ({ children }) => {
  const [blockReason, setBlockReason] = useState(null);
  const [countdown, setCountdown] = useState(10);

  const dispatch = useDispatch();
  const profile = useSelector((state) => state.user.user);
  const navigate = useNavigate();
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Pure function to evaluate profile access
  const evaluateProfileAccess = (user) => {
    if (!user?.type || user.type === USER_TYPE.SUPER_ADMIN) return null;

    const sub = user.subscription;
    if (!sub) return BLOCK_REASON.NO_SUBSCRIPTION;

    const now = new Date();
    if (sub.startDate && new Date(sub.startDate) > now) return BLOCK_REASON.PLAN_NOT_STARTED;
    if (sub.endDate && new Date(sub.endDate) < now) return BLOCK_REASON.SUBSCRIPTION_EXPIRED;
    if (sub.webAccess === false) return BLOCK_REASON.NO_WEB_ACCESS;

    if (Array.isArray(user.permissions) && user.permissions.length === 0) {
      return BLOCK_REASON.NO_PERMISSIONS;
    }

    return null;
  };

  useEffect(() => {
    if (blockReason) return;
    const reason = evaluateProfileAccess(profile);
    if (reason) setBlockReason(reason);
  }, [profile, blockReason]);

  useEffect(() => {
    if (!blockReason) return;

    setCountdown(10);

    const id = setInterval(() => {
      if (!isMountedRef.current) return;
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          const fcmToken = localStorage.getItem(FCM_DEVICE_TOKEN_LS_KEY);
          const cleanup = fcmToken ? NotificationService.unregisterDevice(fcmToken).catch(() => {}) : Promise.resolve();
          cleanup.finally(() => {
            localStorage.removeItem(FCM_DEVICE_TOKEN_LS_KEY);
            clearTableState();
            AuthenticationService.logout().finally(() => {
              if (!isMountedRef.current) return;
              dispatch({ type: "CLEAR_STORE" });
              setBlockReason(null);
              navigate(AUTH_ROUTES.login, { replace: true });
            });
          });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(id); // cleanup on unmount or blockReason change
  }, [blockReason, dispatch, navigate]);

  const config = blockReason ? DIALOG_CONFIG[blockReason] : null;

  return (
    <>
      {!blockReason && children}

      {config && (
        <Dialog
          open={!!blockReason}
          maxWidth="xs"
          fullWidth
          disableEscapeKeyDown
          onClose={() => {}}
          slotProps={{
            paper: {
              sx: {
                borderRadius: 3,
                p: 1,
                boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
              },
            },
            backdrop: {
              sx: {
                backgroundColor: "rgba(0, 0, 0, 0.85)",
                backdropFilter: "none",
              },
            },
          }}
        >
          <DialogTitle sx={{ textAlign: "center", fontSize: 18, fontWeight: 600, pb: 1 }}>{config.title}</DialogTitle>

          <DialogContent sx={{ pt: 0 }}>
            <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.6 }}>
              {config.message(profile?.type)}{" "}
              <Typography component="span" sx={{ fontWeight: 600, color: "#674BF5" }}>
                {countdown}
              </Typography>{" "}
              seconds.
            </Typography>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
